import React, {FC, useEffect, useMemo, useState} from 'react';
import {ErrorInformerPushContextValue, ErrorInformerPushContext} from './ErrorInformerPushContext';
import {ErrorInformerRenderContext} from './ErrorInformerRenderContext';
import {ErrorInformerEmitter} from './errorInformerEmitter';

const autoHideMessagePeriod = 5000;

interface ErrorInformerProviderProps {
	emitter?: ErrorInformerEmitter;
}

export const ErrorInformerProvider: FC<ErrorInformerProviderProps> = props => {
	const {emitter, children} = props;

	const [shownError, setShownError] = useState<null | {id: number}>(null);

	const pushContextValue = useMemo<ErrorInformerPushContextValue>(
		() => ({
			push() {
				setShownError(currentError => {
					if (!currentError) {
						return {id: 1};
					}
					return {id: currentError.id + 1};
				});
			},
		}),
		[],
	);

	// Listed error set form external emitter
	useEffect(() => {
		if (!emitter) {
			return;
		}

		return emitter.on(error => {
			pushContextValue.push(error);
		});
	}, [emitter]);

	// Auto hide an error message
	useEffect(() => {
		if (!shownError) {
			return;
		}

		const timeoutId = window.setTimeout(() => setShownError(null), autoHideMessagePeriod);
		return () => {
			clearTimeout(timeoutId);
		};
	}, [shownError]);

	return (
		<ErrorInformerPushContext.Provider value={pushContextValue}>
			<ErrorInformerRenderContext.Provider value={shownError}>{children}</ErrorInformerRenderContext.Provider>
		</ErrorInformerPushContext.Provider>
	);
};

ErrorInformerProvider.displayName = 'ErrorInformerProvider';
